/**
 * AI Scribe V2 — session persistence
 *
 * Mirrors the live session to sessionStorage on every change so a refresh or the
 * offline screen can recover it, and clears the snapshot the moment the session
 * leaves its live phase (drafting, completed, closed) so a finished visit is
 * never resurrected.
 */

import { useEffect, useRef } from "react";
import { SCRIBE_SESSION_SNAPSHOT_VERSION } from "./constants";
import {
  clearScribeSnapshot,
  writeScribeSnapshot,
} from "./scribe-session-storage";
import { ClinicalEvent, ScribeStatus, ScribeTranscriptLine } from "./types";

interface UseScribeSessionPersistenceParams {
  /** Storage key — the appointment the session belongs to. */
  sessionKey?: string;
  status: ScribeStatus;
  transcript: ScribeTranscriptLine[];
  clinicalEvents: ClinicalEvent[];
  elapsedSeconds: number;
  sessionStartedAt: number;
  noteType?: string;
}

export const useScribeSessionPersistence = ({
  sessionKey,
  status,
  transcript,
  clinicalEvents,
  elapsedSeconds,
  sessionStartedAt,
  noteType,
}: UseScribeSessionPersistenceParams): void => {
  /**
   * Elapsed ticks every second; mirror it so a snapshot records the latest value
   * without re-encrypting on every tick. Writes are driven by real content
   * changes (a new line, event, status or template) instead.
   */
  const elapsedSecondsRef = useRef(elapsedSeconds);
  elapsedSecondsRef.current = elapsedSeconds;

  useEffect(() => {
    if (!sessionKey) return;

    const isLive = status === "LISTENING" || status === "PAUSED";
    if (!isLive) {
      clearScribeSnapshot(sessionKey);
      return;
    }

    void writeScribeSnapshot(sessionKey, {
      version: SCRIBE_SESSION_SNAPSHOT_VERSION,
      status,
      transcript,
      clinicalEvents,
      elapsedSeconds: elapsedSecondsRef.current,
      sessionStartedAt,
      noteType,
    });
  }, [sessionKey, status, transcript, clinicalEvents, sessionStartedAt, noteType]);
};
